import { ApolloError } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  MembershipPlansPreview,
  type MembershipPlansPreviewConfig,
} from '@provision/membership-plans-ui';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResizable } from 'react-resizable-layout';
import { isNullish } from 'remeda';
import {
  BorderVerticalSeparator,
  DynamicIcon,
  EllipsesMenu,
  NamedLayoutBlock,
  Tooltip,
} from 'src/components';
import {
  type BundledActivationFlowConfig,
  type SaveActivationFlowConfigBundleInput,
  useBrands,
  useCopyActivationFlowConfig,
  useGroupRules,
  UserGroupRules,
  useSaveActivationFlowConfigBundle,
} from 'src/graphql';
import { ClientSegmentSelector } from 'src/pages/Settings/memberships/components/client-segment-selector';
import { CopyConfigModal } from 'src/pages/Settings/memberships/components/copy-config-modal';
import { MembershipsConfigViewForm } from 'src/pages/Settings/memberships/components/memberships-config-view-form';
import {
  DEFAULT_PLANS_TAB_VALUES,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import { PricingExportTemplate } from 'src/pages/Settings/memberships/components/pricing-export-template/pricing-export-template';
import { usePricingExport } from 'src/pages/Settings/memberships/hooks/use-pricing-export';
import {
  CONFIG_PANEL_INITIAL_WIDTH_PX,
  CONFIG_PANEL_MAX_WIDTH_PX,
  CONFIG_PANEL_MIN_WIDTH_PX,
  ConfigPanel,
  MembershipsConfigContainer,
  PanelContent,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  PreviewContainer,
  PreviewContent,
  PreviewPlaceholder,
} from 'src/pages/Settings/memberships/styled';
import {
  configLoadingSkeletonSx,
  membershipsBlockContentProps,
  membershipsLayoutSx,
  previewBodySecondarySx,
  previewBodyTertiarySx,
  saveButtonSx,
} from 'src/pages/Settings/memberships/styles';
import {
  formValuesToPreviewConfig,
  fromBranding,
} from 'src/pages/Settings/memberships/utils/form-values-to-preview-config';
import { hasAtLeastOneEnabledProduct } from 'src/pages/Settings/memberships/utils/has-at-least-one-enabled-product';
import { parseMembershipsConfigError } from 'src/pages/Settings/memberships/utils/parse-memberships-config-error';
import { serializeMembershipsConfigPayload } from 'src/pages/Settings/memberships/utils/serialize-config-payload';
import { useGlobalStore } from 'src/store';
import { useMembershipsConfigStore } from 'src/store/memberships-config';
import { useConfirmationModal } from 'src/utils/hooks';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { useScreenDimensions } from 'src/utils/system';

const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Continue without saving?';

const NO_ENABLED_PLANS_CONFIRM_TITLE = 'Cannot save configuration';
const NO_ENABLED_PLANS_CONFIRM_BODY = 'At least one plan must be enabled.';

/** Support image shown in the membership preview contact block (matches Client Gateway). */
const PREVIEW_SUPPORT_IMAGE_URL =
  'https://images.unsplash.com/photo-1758599543154-76ec1c4257df?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlcnNvbiUyMGhlYWRzaG90fGVufDF8fHx8MTc2MjMzOTkzN3ww&ixlib=rb-4.1.0&q=80&w=1080';

const exportIconWrapperSx = {
  width: '1.25rem',
  height: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

type ExportPricingButtonProps = {
  disabled: boolean;
  isExporting: boolean;
  onClick: () => void;
  ariaLabel?: string;
  ariaDescribedBy?: string;
};

const ExportPricingButton = ({
  disabled,
  isExporting,
  onClick,
  ariaLabel = 'Export pricing',
  ariaDescribedBy,
}: ExportPricingButtonProps) => (
  <Tooltip title="Export pricing">
    <span>
      <IconButton
        data-testid="membership-config-export-pricing-button"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        size="small"
        disabled={disabled}
        onClick={onClick}
      >
        <Box sx={exportIconWrapperSx}>
          {isExporting ? (
            <CircularProgress size={16} thickness={5} />
          ) : (
            <DynamicIcon name="file_download" />
          )}
        </Box>
      </IconButton>
    </span>
  </Tooltip>
);

export type MembershipsConfigViewLayoutProps = {
  selectedSegmentId: string | null;
  onSegmentChange: (segmentId: string | null) => void | Promise<void>;
  onCreateConfig: () => void;
  isNewConfig: boolean;
  isLoading: boolean;
  error: unknown;
  data: PlansTabFormValues | null;
  activationFlowConfig: BundledActivationFlowConfig | null;
  formKey: string;
  partnerId: number | null;
  activationFlowConfigId: string | null;
  onSaved?: () => void | Promise<void>;
  showCreateConfig: boolean;
  isCreatingConfig: boolean;
  refreshErrorMessage: string | null;
  onRetryRefresh: () => void;
};

export const MembershipsConfigViewLayout = ({
  selectedSegmentId,
  onSegmentChange,
  onCreateConfig,
  isNewConfig,
  isLoading,
  error,
  data,
  activationFlowConfig,
  formKey,
  partnerId,
  activationFlowConfigId,
  onSaved,
  showCreateConfig,
  isCreatingConfig,
  refreshErrorMessage,
  onRetryRefresh,
}: MembershipsConfigViewLayoutProps) => {
  const { confirm } = useConfirmationModal();
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { brands: brandsFromQuery, loading: segmentsLoading } = useBrands(partnerId);
  const segments = useMemo(
    () =>
      [...(brandsFromQuery ?? [])].sort((a, b) => (b.name ?? '').localeCompare(a.name ?? '')),
    [brandsFromQuery],
  );
  const { checkValidity, loading: groupRulesLoading } = useGroupRules();
  const { saveActivationFlowConfigBundle, loading: saveConfigLoading } =
    useSaveActivationFlowConfigBundle();
  const hasPartner = !isNullish(partnerId);
  const hasSegments = segments.length > 0;
  const initialSegmentSelectedForPartnerRef = useRef<number | null>(null);
  const [requestSaveValues, setRequestSaveValues] = useState<
    (() => Promise<{ values: PlansTabFormValues; reset: () => void } | null>) | null
  >(null);
  const [submitState, setSubmitState] = useState({
    isValid: false,
    isValidating: false,
    isSubmitting: false,
  });
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [liveFormValues, setLiveFormValues] = useState<PlansTabFormValues | null>(null);
  const hasUnsavedChanges = Boolean(requestSaveValues) && isFormDirty;
  const canEditMembershipsConfig =
    !groupRulesLoading && checkValidity([UserGroupRules.AccessToConfigureMemberships]);
  const isReadOnlyMembershipsConfig = !canEditMembershipsConfig;
  const isSaving = submitState.isSubmitting || saveConfigLoading;
  const saveButtonDisabled = isReadOnlyMembershipsConfig || !submitState.isValid || isSaving;
  const showSaveButton = hasUnsavedChanges || isSaving;
  const parsedErrorMessage =
    error instanceof ApolloError
      ? parseMembershipsConfigError(error)
      : 'Failed to load configuration.';
  const { isMobile } = useScreenDimensions();
  const theme = useTheme();
  const savedConfigPanelWidth = useMembershipsConfigStore.use.configPanelWidth();
  const setSavedConfigPanelWidth = useMembershipsConfigStore.use.setConfigPanelWidth();
  const configViewRef = useRef<HTMLDivElement | null>(null);
  const initialConfigPanelWidth = savedConfigPanelWidth ?? CONFIG_PANEL_INITIAL_WIDTH_PX;
  const { position, separatorProps, isDragging } = useResizable({
    axis: 'x',
    initial: initialConfigPanelWidth,
    min: CONFIG_PANEL_MIN_WIDTH_PX,
    max: CONFIG_PANEL_MAX_WIDTH_PX,
    containerRef: configViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: isMobile,
    onResizeEnd: ({ position }) => {
      setSavedConfigPanelWidth(position);
    },
  });

  const configPanelWidth = useMemo(() => {
    const nextWidth = position || initialConfigPanelWidth;
    return Math.min(CONFIG_PANEL_MAX_WIDTH_PX, Math.max(nextWidth, CONFIG_PANEL_MIN_WIDTH_PX));
  }, [initialConfigPanelWidth, position]);

  // When the user is editing, use live form values so the preview updates immediately.
  const previewConfig = useMemo((): MembershipPlansPreviewConfig | null => {
    let fromForm: MembershipPlansPreviewConfig | null = null;
    if (liveFormValues) {
      fromForm = formValuesToPreviewConfig(liveFormValues);
    } else if (data) {
      fromForm = formValuesToPreviewConfig(data);
    }

    if (fromForm) {
      const callForPricePhone = activationFlowConfig?.options?.callForPricingPhone;
      return {
        ...fromForm,
        options: {
          ...fromForm.options,
          ...(callForPricePhone ? { callForPricingPhone: callForPricePhone } : {}),
        },
      };
    }
    if (activationFlowConfig) {
      return activationFlowConfig as unknown as MembershipPlansPreviewConfig;
    }
    return null;
  }, [activationFlowConfig, data, liveFormValues]);

  // Only primary and secondary are used by the membership plans preview.
  const previewBranding = useMemo((): { primaryColor: string; secondaryColor: string } => {
    const fallback = {
      primaryColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary?.main ?? theme.palette.primary.main,
    };
    const fromForm = liveFormValues?.options?.branding ?? data?.options?.branding;

    if (fromForm) {
      return fromBranding(fromForm) ?? fallback;
    }

    const fromConfig = activationFlowConfig?.options?.branding as
      | { primaryColor?: string; secondaryColor?: string }
      | undefined;

    if (fromConfig) {
      return fromBranding(fromConfig) ?? fallback;
    }

    return fallback;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    theme.palette.primary.main,
    theme.palette.secondary?.main,
    liveFormValues?.options?.branding?.primaryColor,
    liveFormValues?.options?.branding?.secondaryColor,
    data?.options?.branding?.primaryColor,
    data?.options?.branding?.secondaryColor,
    activationFlowConfig?.options?.branding?.primaryColor,
    activationFlowConfig?.options?.branding?.secondaryColor,
  ]);

  const debouncedPrimary = useDebouncedValue(previewBranding.primaryColor, 200);
  const debouncedSecondary = useDebouncedValue(previewBranding.secondaryColor, 200);
  const previewBrandingDebounced = useMemo(
    () => ({ primaryColor: debouncedPrimary, secondaryColor: debouncedSecondary }),
    [debouncedPrimary, debouncedSecondary],
  );

  const previewBackgroundImageUrl =
    liveFormValues?.options?.branding?.backgroundImageUrl?.trim() ||
    data?.options?.branding?.backgroundImageUrl?.trim() ||
    undefined;

  const {
    exportGrid,
    exportTemplateData,
    handleExportClick,
    handleTemplateReady,
    isExporting,
    templateRef,
    templateStyle,
  } = usePricingExport({
    activationFlowConfig,
    segments,
    selectedSegmentId,
  });

  const [copyConfigModalOpen, setCopyConfigModalOpen] = useState(false);
  const { copyActivationFlowConfig, loading: copyConfigLoading } =
    useCopyActivationFlowConfig();

  const handleCopyConfig = useCallback(
    async (sourceBrandId: string, targetBrandIds: string[]) => {
      if (!partnerId) return;

      try {
        const response = await copyActivationFlowConfig({
          variables: {
            input: { partnerId, sourceBrandId, targetBrandIds },
          },
        });

        if (!response.data?.copyActivationFlowConfig?.success) {
          showErrorAlert('Unable to copy configuration. Please try again.');
          return;
        }

        showSuccessAlert('Configuration copied successfully.');
        await onSaved?.();
      } catch (err) {
        console.error(err);
        showErrorAlert('Unable to copy configuration. Please try again.');
      }
    },
    [partnerId, copyActivationFlowConfig, showErrorAlert, showSuccessAlert, onSaved],
  );

  const configMenuOptions = useMemo(
    () => [
      {
        label: 'Copy configuration',
        icon: 'content_copy',
        onClick: () => setCopyConfigModalOpen(true),
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (isReadOnlyMembershipsConfig) {
      return;
    }

    if (!selectedSegmentId || !hasPartner) {
      await confirm({
        body: 'Select a partner and client segment before saving.',
      });
      return;
    }

    if (!requestSaveValues) {
      return;
    }

    if (
      isNullish(partnerId) ||
      isNullish(selectedPartnerId) ||
      partnerId !== selectedPartnerId
    ) {
      showErrorAlert('Selected partner changed. Please reselect the partner and try again.');
      return;
    }

    const savePayload = await requestSaveValues();

    if (!savePayload) {
      await confirm({
        title: 'Fix validation errors',
        body: 'Some fields are invalid. Please fix the errors and try again.',
      });
      return;
    }

    const { values, reset } = savePayload;

    if (!hasAtLeastOneEnabledProduct(values)) {
      await confirm({
        title: NO_ENABLED_PLANS_CONFIRM_TITLE,
        body: NO_ENABLED_PLANS_CONFIRM_BODY,
        variant: 'plain',
      });
      return;
    }

    const serializedPayload = serializeMembershipsConfigPayload({
      partnerId,
      brandId: selectedSegmentId,
      activationFlowConfigId,
      formValues: values,
    });

    try {
      const response = await saveActivationFlowConfigBundle({
        variables: {
          input: serializedPayload as SaveActivationFlowConfigBundleInput,
        },
      });

      if (!response.data?.saveActivationFlowConfigBundle?.success) {
        showErrorAlert('Unable to save membership configuration. Please try again.');
        return;
      }

      reset();
      showSuccessAlert('Membership configuration saved.');
      await onSaved?.();
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof ApolloError
          ? parseMembershipsConfigError(error)
          : 'Unable to save membership configuration. Please try again.';
      showErrorAlert(errorMessage);
    }
  }, [
    isReadOnlyMembershipsConfig,
    selectedSegmentId,
    hasPartner,
    requestSaveValues,
    partnerId,
    selectedPartnerId,
    activationFlowConfigId,
    confirm,
    saveActivationFlowConfigBundle,
    showSuccessAlert,
    onSaved,
    showErrorAlert,
  ]);

  const handleSegmentChange = useCallback(
    async (segmentId: string | null) => {
      const normalizedSegmentId = segmentId ? segmentId : null;

      if (normalizedSegmentId === selectedSegmentId) {
        return true;
      }

      if (hasUnsavedChanges) {
        const confirmed = await confirm({
          body: UNSAVED_CHANGES_MESSAGE,
        });

        if (!confirmed) {
          return false;
        }
      }

      await onSegmentChange(normalizedSegmentId);
      return true;
    },
    [confirm, hasUnsavedChanges, onSegmentChange, selectedSegmentId],
  );

  useEffect(() => {
    initialSegmentSelectedForPartnerRef.current = null;
  }, [partnerId]);

  // Reset live form values when segment or form key changes so preview reflects the new config.
  useEffect(() => {
    setLiveFormValues(null);
  }, [formKey, selectedSegmentId]);

  useEffect(() => {
    if (!hasPartner || segmentsLoading || !hasSegments || selectedSegmentId) {
      return;
    }

    if (initialSegmentSelectedForPartnerRef.current === partnerId) {
      return;
    }

    initialSegmentSelectedForPartnerRef.current = partnerId;
    const defaultSegmentId = String(segments[0].brandId);
    void handleSegmentChange(defaultSegmentId);
  }, [
    handleSegmentChange,
    hasSegments,
    hasPartner,
    partnerId,
    segments,
    segmentsLoading,
    selectedSegmentId,
  ]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  let panelContent: JSX.Element | null = null;
  const isInitialSegmentSelectionPending =
    hasPartner &&
    !segmentsLoading &&
    hasSegments &&
    !selectedSegmentId &&
    initialSegmentSelectedForPartnerRef.current !== partnerId;
  const isPanelLoading =
    !hasPartner || segmentsLoading || isLoading || isInitialSegmentSelectionPending;
  const exportButtonDisabled =
    !selectedSegmentId || !activationFlowConfig || isPanelLoading || isExporting;

  if (error) {
    panelContent = (
      <Typography p={2} variant="body2" color="error">
        {parsedErrorMessage}
      </Typography>
    );
  } else if (isPanelLoading) {
    panelContent = (
      <Stack p={2} gap={2} sx={configLoadingSkeletonSx}>
        <Skeleton variant="rounded" height="2.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
        <Skeleton variant="rounded" height="4.5rem" />
      </Stack>
    );
  } else if (!hasSegments) {
    panelContent = (
      <Typography p={2} variant="body2" color="text.secondary">
        No client segments are configured for this partner yet.
      </Typography>
    );
  } else if (!selectedSegmentId) {
    panelContent = (
      <Typography p={2} variant="body2" color="text.secondary">
        Select a client segment to view configuration.
      </Typography>
    );
  } else if (!data && !isNewConfig) {
    panelContent = (
      <Stack p={2} gap={2}>
        <Typography variant="body2" color="text.secondary" align="center">
          No initial configuration found.
        </Typography>
        {showCreateConfig ? (
          <Stack alignItems="center">
            <LoadingButton
              variant="outlined"
              onClick={onCreateConfig}
              loading={isCreatingConfig}
              disabled={isReadOnlyMembershipsConfig || isCreatingConfig}
            >
              Create configuration
            </LoadingButton>
          </Stack>
        ) : null}
      </Stack>
    );
  } else {
    panelContent = (
      <MembershipsConfigViewForm
        key={formKey}
        data={data ?? DEFAULT_PLANS_TAB_VALUES}
        readOnly={isReadOnlyMembershipsConfig}
        onSaveRequestChange={setRequestSaveValues}
        onDirtyChange={setIsFormDirty}
        onSubmitStateChange={setSubmitState}
        onFormValuesChange={setLiveFormValues}
      />
    );
  }

  return (
    <NamedLayoutBlock
      title="Memberships"
      sx={membershipsLayoutSx}
      BlockContentProps={membershipsBlockContentProps}
    >
      <MembershipsConfigContainer
        ref={configViewRef}
        sx={{ userSelect: isDragging ? 'none' : 'auto' }}
      >
        <PreviewContainer backgroundImageUrl={previewBackgroundImageUrl}>
          <PreviewContent>
            {previewConfig ? (
              <MembershipPlansPreview
                config={previewConfig}
                title="Activation Flow Preview"
                subtitle="This is how your membership plans will appear to clients"
                showFaq
                showSetupCheckoutHint
                showContactSupport
                supportImageUrl={PREVIEW_SUPPORT_IMAGE_URL}
                isContinueDisabled={false}
                branding={previewBrandingDebounced}
              />
            ) : (
              <PreviewPlaceholder>
                <Typography variant="h4" gutterBottom fontWeight={700}>
                  Activation Flow Preview
                </Typography>
                <Typography variant="body1" sx={previewBodySecondarySx}>
                  Select a client segment and load or create a configuration to see the
                  preview.
                </Typography>
                <Typography variant="body2" sx={previewBodyTertiarySx}>
                  Use the configuration panel to customize your membership plans, features, and
                  settings.
                </Typography>
              </PreviewPlaceholder>
            )}
          </PreviewContent>
        </PreviewContainer>

        <BorderVerticalSeparator {...separatorProps} />

        <ConfigPanel customWidth={configPanelWidth}>
          <PanelHeader>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <PanelTitle id="memberships-config-panel-title">Configuration</PanelTitle>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <ExportPricingButton
                    disabled={exportButtonDisabled}
                    isExporting={isExporting}
                    onClick={handleExportClick}
                    ariaLabel="Export memberships configuration"
                    ariaDescribedBy="memberships-config-panel-title"
                  />
                  {!groupRulesLoading && !isReadOnlyMembershipsConfig && (
                    <EllipsesMenu options={configMenuOptions} title="More actions" />
                  )}
                </Stack>
              </Stack>
              <ClientSegmentSelector
                segments={segments}
                loading={!hasPartner || segmentsLoading}
                selectedSegmentId={selectedSegmentId}
                onSegmentChange={handleSegmentChange}
              />
              {!groupRulesLoading && isReadOnlyMembershipsConfig ? (
                <Alert severity="warning">
                  <Typography variant="subtitle2">Read-only mode</Typography>
                  <Typography variant="body2">
                    You don&apos;t have permission to edit membership configuration.
                  </Typography>
                </Alert>
              ) : null}
              {refreshErrorMessage ? (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={onRetryRefresh}>
                      Retry refresh
                    </Button>
                  }
                >
                  <Typography variant="body2">{refreshErrorMessage}</Typography>
                </Alert>
              ) : null}
            </Stack>
          </PanelHeader>

          <PanelContent>{panelContent}</PanelContent>

          {isExporting && exportGrid ? (
            <PricingExportTemplate
              ref={templateRef}
              data={exportTemplateData}
              templateStyle={templateStyle}
              onTemplateReady={handleTemplateReady}
            />
          ) : null}

          {showSaveButton ? (
            <PanelFooter>
              <LoadingButton
                variant="contained"
                fullWidth
                onClick={handleSave}
                sx={saveButtonSx}
                disabled={saveButtonDisabled}
                loading={isSaving}
              >
                Save Configuration
              </LoadingButton>
            </PanelFooter>
          ) : null}
        </ConfigPanel>
      </MembershipsConfigContainer>

      <CopyConfigModal
        open={copyConfigModalOpen}
        onClose={() => setCopyConfigModalOpen(false)}
        onSubmit={handleCopyConfig}
        segments={segments}
        loading={copyConfigLoading}
      />
    </NamedLayoutBlock>
  );
};
